import React, { useState, useCallback, useRef, useEffect } from 'react';
import StockInDialog from './StockInDialog';
import StockOutDialog from './StockOutDialog';
import StockTakingDialog from './StockTakingDialog';
import StockCorrectionDialog from './StockCorrectionDialog';

// Operation definitions
interface IOperation {
  id: string;
  name: string;
  description: string;
}

const OPERATIONS: IOperation[] = [
  { id: 'stock-in', name: 'Stock In', description: 'Add inventory from purchases or returns' },
  { id: 'stock-out', name: 'Stock Out', description: 'Remove inventory for sales or transfers' },
  { id: 'stock-taking', name: 'Stock Taking', description: 'Physical inventory count verification' },
  { id: 'stock-correction', name: 'Correction', description: 'Adjust inventory for discrepancies' },
];

// Operation icons (SVG paths)
const OPERATION_ICONS: Record<string, string> = {
  'stock-in': 'M12 4v16m8-8H4', // Plus/arrow down
  'stock-out': 'M5 12h14M12 5l7 7-7 7', // Arrow right
  'stock-taking': 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', // Clipboard check
  'stock-correction': 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', // Edit/pencil
};

// Operation colors
const OPERATION_COLORS: Record<string, { bg: string; hover: string; border: string; text: string; gradient: string }> = {
  'stock-in': {
    bg: 'bg-green-500',
    hover: 'hover:bg-green-600',
    border: 'border-green-600',
    text: 'text-green-600',
    gradient: 'from-green-400 to-green-600'
  },
  'stock-out': {
    bg: 'bg-red-500',
    hover: 'hover:bg-red-600',
    border: 'border-red-600',
    text: 'text-red-600',
    gradient: 'from-red-400 to-red-600'
  },
  'stock-taking': {
    bg: 'bg-blue-500',
    hover: 'hover:bg-blue-600',
    border: 'border-blue-600',
    text: 'text-blue-600',
    gradient: 'from-blue-400 to-blue-600'
  },
  'stock-correction': {
    bg: 'bg-amber-500',
    hover: 'hover:bg-amber-600',
    border: 'border-amber-600',
    text: 'text-amber-600',
    gradient: 'from-amber-400 to-amber-600'
  },
};

const InventoryOperationSelector: React.FC = () => {
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startAngle, setStartAngle] = useState(0);
  const [selectedOperation, setSelectedOperation] = useState<IOperation | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate angle for each operation (4 operations = 90 degrees each)
  const getAngleForIndex = (index: number, total: number) => {
    return (360 / total) * index - 90; // Start from top
  };

  // Calculate position on circle
  const getPositionOnCircle = (angle: number, radius: number) => {
    const radian = (angle * Math.PI) / 180;
    return {
      x: Math.cos(radian) * radius,
      y: Math.sin(radian) * radius,
    };
  };

  // Handle mouse/touch events for rotation
  const getAngleFromEvent = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setStartAngle(getAngleFromEvent(e) - rotation);
  }, [getAngleFromEvent, rotation]);

  const handleMouseMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const currentAngle = getAngleFromEvent(e);
    setRotation(currentAngle - startAngle);
  }, [isDragging, getAngleFromEvent, startAngle]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  const handleOperationClick = (operation: IOperation) => {
    if (!isDragging) {
      setSelectedOperation(operation);
    }
  };

  const handleCloseDialog = () => {
    setSelectedOperation(null);
  };

  // Responsive dimensions
  const containerSize = isMobile ? 280 : 400;
  const radius = isMobile ? 90 : 140;
  const centerSize = isMobile ? 'w-20 h-20' : 'w-28 h-28';
  const buttonSize = isMobile ? 'w-16 h-16' : 'w-24 h-24';
  const iconSize = isMobile ? 'w-5 h-5' : 'w-8 h-8';
  const outerRingRadius = isMobile ? 130 : 190;

  return (
    <div className="space-y-6 overflow-hidden">
      {/* Instructions */}
      <div className="text-center text-gray-600 dark:text-gray-400 text-sm">
        <p>Drag to rotate the wheel. Click on an operation to begin.</p>
      </div>

      {/* Rotating Wheel - positioned to the right */}
      <div className="flex justify-end overflow-hidden">
        <div
          ref={containerRef}
          className="relative select-none flex-shrink-0"
          style={{ width: `${containerSize}px`, height: `${containerSize}px` }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
      >
        {/* Center circle */}
        <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${centerSize} bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full shadow-lg flex items-center justify-center z-10`}>
          <div className="text-center text-white">
            <svg className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} mx-auto mb-1`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium`}>Inventory</span>
          </div>
        </div>

        {/* Operations around the circle */}
        <div
          className="absolute inset-0 transition-transform"
          style={{
            transform: `rotate(${rotation}deg)`,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
        >
          {OPERATIONS.map((operation, index) => {
            const angle = getAngleForIndex(index, OPERATIONS.length);
            const pos = getPositionOnCircle(angle, radius);
            const colors = OPERATION_COLORS[operation.id];
            const iconPath = OPERATION_ICONS[operation.id];

            return (
              <div
                key={operation.id}
                className="absolute"
                style={{
                  left: `calc(50% + ${pos.x}px)`,
                  top: `calc(50% + ${pos.y}px)`,
                  transform: `translate(-50%, -50%) rotate(${-rotation}deg)`,
                }}
              >
                <button
                  onClick={() => handleOperationClick(operation)}
                  className={`group flex flex-col items-center justify-center ${buttonSize} bg-gradient-to-br ${colors.gradient} rounded-full shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg`}
                  title={operation.description}
                >
                  <svg
                    className={`${iconSize} text-white`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={iconPath}
                    />
                  </svg>
                  <span className={`${isMobile ? 'text-[9px]' : 'text-xs'} text-white font-medium mt-1 max-w-full truncate px-1`}>
                    {operation.name}
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        {/* Decorative outer ring */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          <circle
            cx={containerSize / 2}
            cy={containerSize / 2}
            r={outerRingRadius}
            fill="none"
            stroke="url(#inventoryGradient)"
            strokeWidth="2"
            strokeDasharray="10 5"
            opacity="0.3"
          />
          <defs>
            <linearGradient id="inventoryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
        </svg>
        </div>
      </div>

      {/* Operation Legend */}
      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        {OPERATIONS.map((operation) => {
          const colors = OPERATION_COLORS[operation.id];
          return (
            <button
              key={operation.id}
              onClick={() => setSelectedOperation(operation)}
              className={`flex items-center gap-2 p-3 rounded-lg border-2 ${colors.border} bg-white dark:bg-gray-800 hover:bg-gray-50 transition-colors`}
            >
              <div className={`w-4 h-4 rounded-full ${colors.bg}`} />
              <div className="text-left">
                <span className={`text-sm font-medium ${colors.text}`}>{operation.name}</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">{operation.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Dialogs */}
      {selectedOperation?.id === 'stock-in' && (
        <StockInDialog onClose={handleCloseDialog} />
      )}
      {selectedOperation?.id === 'stock-out' && (
        <StockOutDialog onClose={handleCloseDialog} />
      )}
      {selectedOperation?.id === 'stock-taking' && (
        <StockTakingDialog onClose={handleCloseDialog} />
      )}
      {selectedOperation?.id === 'stock-correction' && (
        <StockCorrectionDialog onClose={handleCloseDialog} />
      )}
    </div>
  );
};

export default InventoryOperationSelector;
